import { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
  fetchUsers,
  deleteUser,
  topUpUserCredits,
  grantMembership,
  revokeMembership,
} from '../lib/api';
import type { AdminUser, MembershipPlanType } from '../types/api';
import CreditTopUpDialog from './CreditTopUpDialog';
import GrantMembershipDialog from './GrantMembershipDialog';

const PAGE_SIZE = 50;

function formatDate(iso: string | null | undefined): string {
  if (!iso) return '—';
  return new Date(iso).toLocaleDateString('en-GB', {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
  });
}

function formatUnix(ts: number | undefined): string {
  if (!ts) return '—';
  return new Date(ts * 1000).toLocaleDateString('en-GB', {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
  });
}

function SubscriptionBadge({ source, planType }: { source: string; planType: string }) {
  const isLifetime = planType === 'lifetime';
  return (
    <span
      className={`inline-flex rounded-md px-2 py-0.5 text-xs font-medium ring-1 ring-inset ${
        isLifetime
          ? 'bg-purple-50 text-purple-700 ring-purple-600/20'
          : 'bg-green-50 text-green-700 ring-green-600/20'
      }`}
    >
      {planType} · {source}
    </span>
  );
}

function StripeBadge({ status }: { status: string }) {
  const ok = status === 'active' || status === 'trialing';
  return (
    <span
      className={`inline-flex rounded-md px-2 py-0.5 text-xs font-medium ring-1 ring-inset ${
        ok
          ? 'bg-green-50 text-green-700 ring-green-600/20'
          : 'bg-gray-100 text-gray-600 ring-gray-500/20'
      }`}
    >
      {status}
    </span>
  );
}

interface DeleteDialogProps {
  user: AdminUser;
  onConfirm: () => void;
  onCancel: () => void;
  isPending: boolean;
}

function DeleteDialog({ user, onConfirm, onCancel, isPending }: DeleteDialogProps) {
  return (
    <div className="fixed inset-0 bg-gray-500 bg-opacity-75 flex items-center justify-center z-50">
      <div className="bg-white rounded-lg shadow-xl p-6 w-full max-w-md">
        <h3 className="text-lg font-semibold text-gray-900 mb-2">Delete User</h3>
        <p className="text-sm text-gray-600 mb-1">
          Are you sure you want to permanently delete:
        </p>
        <p className="text-sm font-medium text-gray-900 mb-4">
          {user.dbUser.email} ({user.dbUser.id.slice(0, 8)}…)
        </p>
        <p className="text-sm text-gray-500 mb-6">
          This will cancel their Stripe subscriptions, remove them from the database,
          and delete their Supabase account. This action cannot be undone.
        </p>
        <div className="flex justify-end gap-3">
          <button
            onClick={onCancel}
            disabled={isPending}
            className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50 disabled:opacity-50"
          >
            Cancel
          </button>
          <button
            onClick={onConfirm}
            disabled={isPending}
            className="px-4 py-2 text-sm font-medium text-white bg-red-600 rounded-md hover:bg-red-700 disabled:opacity-50"
          >
            {isPending ? 'Deleting…' : 'Delete'}
          </button>
        </div>
      </div>
    </div>
  );
}

interface RevokeDialogProps {
  user: AdminUser;
  onConfirm: () => void;
  onCancel: () => void;
  isPending: boolean;
}

function RevokeDialog({ user, onConfirm, onCancel, isPending }: RevokeDialogProps) {
  return (
    <div className="fixed inset-0 bg-gray-500 bg-opacity-75 flex items-center justify-center z-50">
      <div className="bg-white rounded-lg shadow-xl p-6 w-full max-w-md">
        <h3 className="text-lg font-semibold text-gray-900 mb-2">Remove Pro</h3>
        <p className="text-sm text-gray-600 mb-1">
          Remove the dashboard-granted membership for:
        </p>
        <p className="text-sm font-medium text-gray-900 mb-4">
          {user.dbUser.email}
        </p>
        <p className="text-sm text-gray-500 mb-6">
          This only removes <span className="font-medium">athilioDashboard</span>{' '}
          subscriptions (admin / web grants). App Store, Play Store and Stripe
          subscriptions are left untouched.
        </p>
        <div className="flex justify-end gap-3">
          <button
            onClick={onCancel}
            disabled={isPending}
            className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50 disabled:opacity-50"
          >
            Cancel
          </button>
          <button
            onClick={onConfirm}
            disabled={isPending}
            className="px-4 py-2 text-sm font-medium text-white bg-red-600 rounded-md hover:bg-red-700 disabled:opacity-50"
          >
            {isPending ? 'Removing…' : 'Remove Pro'}
          </button>
        </div>
      </div>
    </div>
  );
}

export default function UsersTable() {
  const [page, setPage] = useState(1);
  const [deleteTarget, setDeleteTarget] = useState<AdminUser | null>(null);
  const [topUpTarget, setTopUpTarget] = useState<AdminUser | null>(null);
  const [grantTarget, setGrantTarget] = useState<AdminUser | null>(null);
  const [revokeTarget, setRevokeTarget] = useState<AdminUser | null>(null);
  const queryClient = useQueryClient();

  const { data, isLoading, isError, error } = useQuery({
    queryKey: ['admin-users', page, PAGE_SIZE],
    queryFn: () => fetchUsers(page, PAGE_SIZE),
    staleTime: 60_000,
  });

  const deleteMutation = useMutation({
    mutationFn: (userId: string) => deleteUser(userId),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['admin-users'] });
      setDeleteTarget(null);
    },
  });

  const topUpMutation = useMutation({
    mutationFn: ({
      userId,
      credits,
      note,
    }: {
      userId: string;
      credits: number;
      note?: string;
    }) => topUpUserCredits(userId, credits, note),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['admin-users'] });
      setTopUpTarget(null);
    },
  });

  const grantMutation = useMutation({
    mutationFn: ({
      userId,
      planType,
      months,
    }: {
      userId: string;
      planType: MembershipPlanType;
      months: number;
    }) => grantMembership(userId, planType, months),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['admin-users'] });
      setGrantTarget(null);
    },
  });

  const revokeMutation = useMutation({
    mutationFn: (userId: string) => revokeMembership(userId),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['admin-users'] });
      setRevokeTarget(null);
    },
  });

  if (isLoading) {
    return <p className="text-sm text-gray-500 py-8 text-center">Loading users…</p>;
  }

  if (isError) {
    return (
      <p className="text-sm text-red-600 py-8 text-center">
        {error instanceof Error ? error.message : 'Failed to load users'}
      </p>
    );
  }

  const { users, total, limit } = data!;
  const start = (page - 1) * limit + 1;
  const end = Math.min(page * limit, total);
  const totalPages = Math.ceil(total / limit);

  return (
    <>
      <div className="overflow-x-auto">
        <table className="min-w-full divide-y divide-gray-200">
          <thead className="bg-gray-50">
            <tr>
              {['User', 'DB / Plan', 'Credits', 'Supabase', 'Stripe', 'Actions'].map((h) => (
                <th
                  key={h}
                  className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider"
                >
                  {h}
                </th>
              ))}
            </tr>
          </thead>
          <tbody className="bg-white divide-y divide-gray-200">
            {users.map((u) => (
              <tr key={u.dbUser.id} className="hover:bg-gray-50">
                {/* User */}
                <td className="px-4 py-3">
                  <p className="text-sm font-medium text-gray-900">{u.dbUser.email}</p>
                  <p className="text-xs text-gray-500">{u.dbUser.displayName}</p>
                  <span className="inline-flex mt-1 rounded px-1.5 py-0.5 text-xs bg-gray-100 text-gray-600">
                    {u.dbUser.provider}
                  </span>
                </td>

                {/* DB / Plan */}
                <td className="px-4 py-3">
                  <p className="text-xs text-gray-500 mb-1">{formatDate(u.dbUser.createdAt)}</p>
                  {u.localSubscription ? (
                    <SubscriptionBadge
                      planType={u.localSubscription.planType}
                      source={u.localSubscription.source}
                    />
                  ) : (
                    <span className="text-xs text-gray-400">No subscription</span>
                  )}
                  {u.localSubscription?.isTrial && (
                    <span className="ml-1 text-xs text-yellow-600">trial</span>
                  )}
                </td>

                {/* Credits */}
                <td className="px-4 py-3">
                  {u.credits?.enabled ? (
                    <>
                      <p
                        className={`text-sm font-medium ${
                          u.credits.balance <= 0 ? 'text-red-600' : 'text-gray-900'
                        }`}
                      >
                        {u.credits.balance} credits
                      </p>
                      <p className="text-xs text-gray-400">
                        {u.credits.grantRemaining} grant ·{' '}
                        {u.credits.purchasedRemaining} purchased
                      </p>
                      {u.credits.refillsAt && (
                        <p className="text-xs text-gray-400">
                          Refills {formatDate(u.credits.refillsAt)}
                        </p>
                      )}
                    </>
                  ) : (
                    <span className="text-xs text-gray-400">—</span>
                  )}
                </td>

                {/* Supabase */}
                <td className="px-4 py-3">
                  {u.supabaseUser ? (
                    <>
                      <div className="flex items-center gap-1 mb-1">
                        {u.supabaseUser.emailConfirmedAt ? (
                          <span className="text-green-600 text-xs font-medium">✓ confirmed</span>
                        ) : (
                          <span className="text-red-500 text-xs">✗ unconfirmed</span>
                        )}
                      </div>
                      <p className="text-xs text-gray-400">
                        Last sign in: {formatDate(u.supabaseUser.lastSignInAt)}
                      </p>
                    </>
                  ) : (
                    <span className="text-xs text-red-500">✗ not found</span>
                  )}
                </td>

                {/* Stripe */}
                <td className="px-4 py-3">
                  {u.stripeCustomer ? (
                    <>
                      <p className="text-xs font-mono text-gray-500 mb-1">
                        {u.stripeCustomer.id.slice(0, 18)}…
                      </p>
                      {u.stripeCustomer.subscriptions.length > 0 ? (
                        <div className="space-y-1">
                          {u.stripeCustomer.subscriptions.map((s) => (
                            <div key={s.id}>
                              <StripeBadge status={s.status} />
                              <span className="ml-1 text-xs text-gray-400">
                                until {formatUnix(s.currentPeriodEnd)}
                              </span>
                            </div>
                          ))}
                        </div>
                      ) : (
                        <span className="text-xs text-gray-400">No subscriptions</span>
                      )}
                    </>
                  ) : (
                    <span className="text-xs text-gray-400">—</span>
                  )}
                </td>

                {/* Actions */}
                <td className="px-4 py-3">
                  <div className="flex flex-col gap-2">
                    <button
                      onClick={() => setTopUpTarget(u)}
                      className="px-3 py-1.5 text-xs font-medium text-white bg-indigo-600 rounded hover:bg-indigo-700"
                    >
                      Top up
                    </button>
                    <button
                      onClick={() => setGrantTarget(u)}
                      className="px-3 py-1.5 text-xs font-medium text-white bg-purple-600 rounded hover:bg-purple-700"
                    >
                      Grant Pro
                    </button>
                    {u.localSubscription && (
                      <button
                        onClick={() => setRevokeTarget(u)}
                        className="px-3 py-1.5 text-xs font-medium text-purple-700 bg-white border border-purple-300 rounded hover:bg-purple-50"
                      >
                        Remove Pro
                      </button>
                    )}
                    <button
                      onClick={() => setDeleteTarget(u)}
                      className="px-3 py-1.5 text-xs font-medium text-white bg-red-600 rounded hover:bg-red-700"
                    >
                      Delete
                    </button>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* Pagination */}
      <div className="flex items-center justify-between px-4 py-3 border-t border-gray-200 bg-white">
        <p className="text-sm text-gray-500">
          Showing {start}–{end} of {total} users
        </p>
        <div className="flex gap-2">
          <button
            onClick={() => setPage((p) => p - 1)}
            disabled={page === 1}
            className="px-3 py-1.5 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded hover:bg-gray-50 disabled:opacity-40 disabled:cursor-not-allowed"
          >
            Previous
          </button>
          <button
            onClick={() => setPage((p) => p + 1)}
            disabled={page >= totalPages}
            className="px-3 py-1.5 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded hover:bg-gray-50 disabled:opacity-40 disabled:cursor-not-allowed"
          >
            Next
          </button>
        </div>
      </div>

      {topUpTarget && (
        <CreditTopUpDialog
          user={topUpTarget}
          onConfirm={(credits, note) =>
            topUpMutation.mutate({
              userId: topUpTarget.dbUser.id,
              credits,
              note: note || undefined,
            })
          }
          onCancel={() => setTopUpTarget(null)}
          isPending={topUpMutation.isPending}
        />
      )}

      {grantTarget && (
        <GrantMembershipDialog
          user={grantTarget}
          onConfirm={(planType, months) =>
            grantMutation.mutate({
              userId: grantTarget.dbUser.id,
              planType,
              months,
            })
          }
          onCancel={() => setGrantTarget(null)}
          isPending={grantMutation.isPending}
        />
      )}

      {revokeTarget && (
        <RevokeDialog
          user={revokeTarget}
          onConfirm={() => revokeMutation.mutate(revokeTarget.dbUser.id)}
          onCancel={() => setRevokeTarget(null)}
          isPending={revokeMutation.isPending}
        />
      )}

      {/* Delete dialog */}
      {deleteTarget && (
        <DeleteDialog
          user={deleteTarget}
          onConfirm={() => deleteMutation.mutate(deleteTarget.dbUser.id)}
          onCancel={() => setDeleteTarget(null)}
          isPending={deleteMutation.isPending}
        />
      )}
    </>
  );
}
